import React, { memo } from 'react'
import { alpha, Stack, TableRow, Typography } from '@mui/material'
import { switchClasses } from '@mui/material/Switch'
import { Toggle } from '../../../shared/components/Toggle/Toggle'
import { COLORS } from '../../../style/colors'
import AdvancedTextarea from '../../../shared/components/AdvancedTextarea'
import { DataTableText } from '../../../shared/components/DataTable'
import { BodyCell, ClickableOverflowText, ScrollableFormattedText } from './CommentsTableCells'
import { CommentsViewColumnId, CommentsViewComment, CommentsViewNavigationTarget } from '../CommentsView.types'
import { columnWidths, formatCommentDate, normalizeAction } from '../CommentsView.helpers'

type CommentsTableRowProps = {
    comment: CommentsViewComment
    commentIndex: number
    commentsLength: number
    displayNumber: number
    visibleColumnIds: CommentsViewColumnId[]
    typeLabel: string
    locationLabel: string
    locationTarget: CommentsViewNavigationTarget | null
    isHighlighted: boolean
    isEditingDisabled: boolean
    isSaving: boolean
    onLocationClick: (target: CommentsViewNavigationTarget, commentId: string) => void
    onResolvedChange: (comment: CommentsViewComment, isResolved: boolean) => Promise<void>
    onActionSave: (comment: CommentsViewComment, action: string) => Promise<void>
}

export const CommentsTableRow = memo(
    ({
        comment,
        commentIndex,
        commentsLength,
        displayNumber,
        visibleColumnIds,
        typeLabel,
        locationLabel,
        locationTarget,
        isHighlighted,
        isEditingDisabled,
        isSaving,
        onLocationClick,
        onResolvedChange,
        onActionSave,
    }: CommentsTableRowProps) => {
        const isFirstRow = commentIndex === 0
        const isLastRow = commentIndex === commentsLength - 1
        const isDeletedLocation = !locationTarget
        const firstVisibleColumnId = visibleColumnIds[0]
        const lastVisibleColumnId = visibleColumnIds[visibleColumnIds.length - 1]

        const handleResolvedToggle = async (_event: unknown, checked: boolean) => {
            if (comment.isResolved === checked || isSaving) {
                return
            }

            await onResolvedChange(comment, checked)
        }

        return (
            <TableRow
                data-highlight-id={comment._id}
                sx={{
                    backgroundColor: isHighlighted
                        ? 'figma.primary3'
                        : commentIndex % 2 === 0
                          ? 'figma.grayscale.table1'
                          : 'figma.grayscale.table2',
                    cursor: isSaving ? 'progress' : 'default',
                    opacity: isSaving ? 0.55 : 1,
                    transition: 'background-color 0.6s ease, opacity 0.2s ease',
                    '& .MuiTableCell-root': {
                        pointerEvents: isSaving ? 'none' : 'auto',
                    },
                }}
            >
                {visibleColumnIds.includes('user') && (
                    <BodyCell
                        width={columnWidths.user}
                        isFirstColumn={firstVisibleColumnId === 'user'}
                        isLastColumn={lastVisibleColumnId === 'user'}
                        isFirstRow={isFirstRow}
                        isLastRow={isLastRow}
                    >
                        <Stack direction="row" alignItems="center" gap={2}>
                            <Typography variant="22x300" color="figma.grayscale.tier2">
                                {displayNumber}.
                            </Typography>
                            <Typography variant="14x400" color="figma.grayscale.textHier2">
                                {comment.user?.name ?? 'Unknown User'}
                            </Typography>
                        </Stack>
                    </BodyCell>
                )}

                {visibleColumnIds.includes('comment') && (
                    <BodyCell
                        width={columnWidths.comment}
                        isFirstColumn={firstVisibleColumnId === 'comment'}
                        isLastColumn={lastVisibleColumnId === 'comment'}
                        isFirstRow={isFirstRow}
                        isLastRow={isLastRow}
                    >
                        <ScrollableFormattedText text={comment.text} />
                    </BodyCell>
                )}

                {visibleColumnIds.includes('type') && (
                    <BodyCell
                        width={columnWidths.type}
                        isFirstColumn={firstVisibleColumnId === 'type'}
                        isLastColumn={lastVisibleColumnId === 'type'}
                        isFirstRow={isFirstRow}
                        isLastRow={isLastRow}
                    >
                        <Typography variant="14x400" color="figma.grayscale.textHier2">
                            {typeLabel}
                        </Typography>
                    </BodyCell>
                )}

                {visibleColumnIds.includes('location') && (
                    <BodyCell
                        width={columnWidths.location}
                        isFirstColumn={firstVisibleColumnId === 'location'}
                        isLastColumn={lastVisibleColumnId === 'location'}
                        isFirstRow={isFirstRow}
                        isLastRow={isLastRow}
                    >
                        {locationTarget ? (
                            <ClickableOverflowText
                                text={locationLabel}
                                onClick={() => onLocationClick(locationTarget, comment._id)}
                            />
                        ) : (
                            <DataTableText
                                text={locationLabel}
                                color={isDeletedLocation ? 'figma.grayscale.tier4' : undefined}
                            />
                        )}
                    </BodyCell>
                )}

                {visibleColumnIds.includes('date') && (
                    <BodyCell
                        width={columnWidths.date}
                        isFirstColumn={firstVisibleColumnId === 'date'}
                        isLastColumn={lastVisibleColumnId === 'date'}
                        isFirstRow={isFirstRow}
                        isLastRow={isLastRow}
                    >
                        <Typography variant="14x400" color="figma.grayscale.textHier2">
                            {formatCommentDate(comment.createdAt)}
                        </Typography>
                    </BodyCell>
                )}

                {visibleColumnIds.includes('updated') && (
                    <BodyCell
                        width={columnWidths.updated}
                        isFirstColumn={firstVisibleColumnId === 'updated'}
                        isLastColumn={lastVisibleColumnId === 'updated'}
                        isFirstRow={isFirstRow}
                        isLastRow={isLastRow}
                    >
                        <Typography variant="14x400" color="figma.grayscale.textHier2">
                            {formatCommentDate(comment.updatedAt)}
                        </Typography>
                    </BodyCell>
                )}

                {visibleColumnIds.includes('resolved') && (
                    <BodyCell
                        width={columnWidths.resolved}
                        align="center"
                        isFirstColumn={firstVisibleColumnId === 'resolved'}
                        isLastColumn={lastVisibleColumnId === 'resolved'}
                        isFirstRow={isFirstRow}
                        isLastRow={isLastRow}
                    >
                        <Toggle
                            checked={Boolean(comment.isResolved)}
                            disabled={isEditingDisabled || isSaving}
                            onChange={(_, checked) => void handleResolvedToggle(_, checked)}
                            sx={{
                                margin: 0,
                                [`& .${switchClasses.track}`]: {
                                    backgroundColor: alpha(COLORS.lipstickRed, 0.22),
                                },
                                [`& .${switchClasses.switchBase}.${switchClasses.checked} + .${switchClasses.track}`]: {
                                    backgroundColor: COLORS.caribbeanGreen,
                                },
                            }}
                        />
                    </BodyCell>
                )}

                {visibleColumnIds.includes('action') && (
                    <BodyCell
                        width={columnWidths.action}
                        verticalAlign="top"
                        isFirstColumn={firstVisibleColumnId === 'action'}
                        isLastColumn={lastVisibleColumnId === 'action'}
                        isFirstRow={isFirstRow}
                        isLastRow={isLastRow}
                    >
                        <AdvancedTextarea
                            value={comment.action ?? ''}
                            placeholder="Enter action…"
                            disabled={isEditingDisabled}
                            isSaving={isSaving}
                            onSave={async (nextValue) => {
                                const draftValue = normalizeAction(nextValue)
                                const currentValue = normalizeAction(comment.action)

                                if (draftValue === currentValue || isSaving) {
                                    return
                                }

                                await onActionSave(comment, draftValue)
                            }}
                            maxHeight={isEditingDisabled ? 96 : 48}
                            displayTextSx={{
                                cursor: isEditingDisabled ? 'default' : 'text',
                            }}
                        />
                    </BodyCell>
                )}
            </TableRow>
        )
    }
)
